'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ResourceTypeHandle } from '@toolproof-core/genesis/_contract/_index';
import type { SpaceAdapterResourceTypeInputSpec } from '@toolproof-core/visualization/adapter/_contract/adapter';
import type { CosmosData } from '@toolproof-core/visualization/derivation/_contract/derivation';
import type { SpaceRuntimeProps } from '@toolproof-core/visualization/runtime/space-runtime';
import { deriveEffectiveFrameFromOutput } from '@toolproof-core/visualization/_pure/transition-engine';

import type { SpaceAdapterDescriptor } from '@/_contract/types';
import CosmosDataProvider, { type CosmosDataWithTimestamps } from '@/CosmosDataProvider';
import { getSpaceAdapterDescriptorBySlug } from '@/adapters/_@shared/spaceAdapterRegistry';
import { useAdapterRuntimeEffects } from '@/drivers/nativeAdapterDriver/useAdapterRuntimeEffects';
import { useAdapterRuntimeTicks } from '@/drivers/nativeAdapterDriver/useAdapterRuntimeTicks';
import { useNativeAdapterSession } from '@/drivers/nativeAdapterDriver/useNativeAdapterSession';
import { useSupplementalCosmosData } from '@/drivers/nativeAdapterDriver/useSupplementalCosmosData';
import type { RuntimeCallbackCarrier } from '@/drivers/nativeAdapterDriver/_pure/withRuntimeStateChangeCallback';
import { parseCosmosRoute } from '@/_pure/cosmosRoute';

export type PortalPreview = NonNullable<SpaceRuntimeProps['portalPreview']>;
export type PortalFocus = { portalKey: string; destination: string };

type ErasedDescriptor = SpaceAdapterDescriptor<
	ResourceTypeHandle,
	CosmosData<ResourceTypeHandle>,
	RuntimeCallbackCarrier<unknown>,
	unknown
>;

const inputSpecByDescriptor = new WeakMap<ErasedDescriptor, SpaceAdapterResourceTypeInputSpec<ResourceTypeHandle>>();

function inputSpecOf(descriptor: ErasedDescriptor) {
	let spec = inputSpecByDescriptor.get(descriptor);
	if (!spec) {
		spec = descriptor.baseDescriptor.getResourceTypeInputSpec();
		inputSpecByDescriptor.set(descriptor, spec);
	}
	return spec;
}

const PLAY_ACTION = { overlayIntentKind: 'action-triggered', actionId: 'episode-play-pause' } as const;
/** How long a finished preview rests on its last view before starting over. */
const REPLAY_HOLD_MS = 3000;
/** Previews of long episodes start over after this, so a portal keeps showing the episode's opening. */
const REPLAY_AFTER_MS = 60_000;
const navigateNowhere = () => {};

/**
 * Plays the episode silently in its own session, as a viewer who pressed Play would see it, and loops it.
 * Its effect owner is private, so narration runs muted and nothing reaches the hub's own session.
 */
function EpisodePreviewSession(props: {
	descriptor: ErasedDescriptor;
	handle: string;
	portalKey: string;
	cosmosData: CosmosDataWithTimestamps;
	loading: boolean;
	onPreview: (preview: PortalPreview | undefined) => void;
}) {
	const { descriptor, handle, portalKey, cosmosData, loading, onPreview } = props;
	const base = descriptor.baseDescriptor;
	const presentation = descriptor.episodePresentation;
	const supplemental = useSupplementalCosmosData(descriptor);
	const supplementalReady = !descriptor.loadSupplementalCosmosData
		|| (supplemental?.descriptor === descriptor && !supplemental.error);
	const data = useMemo(() => ({
		...base.buildCosmosData(cosmosData.resourcesByType, cosmosData.timestampByHandleByType),
		...(supplemental?.descriptor === descriptor ? supplemental.data : undefined),
	}), [base, descriptor, cosmosData.resourcesByType, cosmosData.timestampByHandleByType, supplemental]);
	const owner = useMemo(() => descriptor.createRuntimeEffectOwner?.(), [descriptor]);
	const pending = loading || !supplementalReady;
	const session = useNativeAdapterSession({
		descriptor, cosmosData: data, loading: pending, episodeHandle: handle, episodeSoundDefault: false,
		stopRuntimeEffects: () => owner?.dispose?.(), navigateToEpisode: navigateNowhere,
	});
	const { runtimeState, runtimeConfig, derivationState, routeError, playbackActive, animationSuspended,
		playbackResetKey } = session;
	const output = useMemo(() => {
		if (pending || routeError) return undefined;
		try {
			const output = base.adapter.createSpaceDeriver()({ spaceKey: base.spaceKey, cosmosData: data,
				runtimeState: derivationState.runtimeState, config: runtimeConfig });
			// Folding the transitions first rejects a plan that cannot play.
			deriveEffectiveFrameFromOutput(output);
			return output;
		} catch (failure) {
			console.warn(`[PortalPreview] ${handle} could not be derived:`, failure);
			return undefined;
		}
	}, [base, data, derivationState, runtimeConfig, pending, routeError, handle]);

	useAdapterRuntimeEffects({
		owner, config: runtimeConfig, runtimeState, cosmosData: data,
		playbackActive, animationSuspended, loading: pending, routeError,
	});
	const runtimeController = session.runtimeController;
	useAdapterRuntimeTicks({
		controller: runtimeController,
		intervalMs: pending || routeError || playbackActive || animationSuspended ? undefined
			: runtimeController.getRuntimeTickIntervalMs?.({ config: runtimeConfig, runtimeState, cosmosData: data }),
		isSuspended: session.isAnimationSuspended, onTick: session.onRuntimeTick,
	});

	// Session callbacks change every render; the preview hands out stable ones that reach the latest.
	const sessionRef = useRef(session);
	useLayoutEffect(() => { sessionRef.current = session; });
	const playback = useMemo(() => ({
		resetKey: playbackResetKey,
		onTransitionComplete: () => sessionRef.current.onDerivationTransitionComplete(),
		onTransitionPhaseComplete: (key: string) => sessionRef.current.onDerivationTransitionPhaseComplete(key),
		onPlaybackActivityChange: (active: boolean) => sessionRef.current.onPlaybackActivityChange(active),
	}), [playbackResetKey]);

	const status = presentation?.playback(runtimeState);
	const playing = Boolean(status?.isPlaying);
	const finished = !playing && !playbackActive && Boolean(presentation?.settled(runtimeState));
	const startedKey = useRef<number>(undefined);
	const [playedKey, setPlayedKey] = useState<number>();
	if (playing && playedKey !== playbackResetKey) setPlayedKey(playbackResetKey);
	useEffect(() => {
		if (!output || startedKey.current === playbackResetKey) return;
		startedKey.current = playbackResetKey;
		if (!playing) sessionRef.current.reduceAdapterIntent(PLAY_ACTION);
	}, [output, playbackResetKey, playing]);
	const replayDue = finished && playedKey === playbackResetKey;
	const ready = output !== undefined;
	useEffect(() => {
		if (!ready || startedKey.current !== playbackResetKey) return;
		const replay = () => sessionRef.current.navigateEpisode(handle, true);
		const timer = setTimeout(replay, replayDue ? REPLAY_HOLD_MS : REPLAY_AFTER_MS);
		return () => clearTimeout(timer);
	}, [ready, playbackResetKey, replayDue, handle]);

	useEffect(() => {
		onPreview(output ? { portalKey, output, playback } : undefined);
	}, [output, portalKey, playback, onPreview]);
	useEffect(() => () => onPreview(undefined), [onPreview]);
	return null;
}

/** Plays the episode a focused portal leads to, silently and looping; other destinations get no preview. */
export function PortalPreviewSource(props: {
	focus: PortalFocus | undefined;
	onPreview: (preview: PortalPreview | undefined) => void;
}) {
	const { focus, onPreview } = props;
	const destination = focus?.destination;
	const target = useMemo(() => {
		if (!destination) return undefined;
		const route = parseCosmosRoute(destination);
		if (route.kind !== 'space' || !route.episodeHandle) return undefined;
		const descriptor = getSpaceAdapterDescriptorBySlug(route.spaceSlug) as ErasedDescriptor | undefined;
		if (!descriptor?.episodePresentation) return undefined;
		return { descriptor, handle: route.episodeHandle, spec: inputSpecOf(descriptor) };
	}, [destination]);
	useEffect(() => {
		if (!target) onPreview(undefined);
	}, [target, onPreview]);
	if (!focus || !target) return null;
	return (
		<CosmosDataProvider key={target.descriptor.baseDescriptor.spaceKey} resourceTypeInputSpec={target.spec}>
			{({ cosmosData, loading }) => (
				<EpisodePreviewSession key={focus.portalKey} descriptor={target.descriptor} handle={target.handle}
					portalKey={focus.portalKey} cosmosData={cosmosData} loading={loading} onPreview={onPreview} />
			)}
		</CosmosDataProvider>
	);
}
